import { Text, Container, Box, Flex, Button, SxProp } from 'theme-ui';
import {
  useCart,
  useAnalytics,
  useCheckout,
  OrderBig,
  useProduct,
  LineItem,
  VariantStockAvailability,
} from '@chordcommerce/react-autonomy';
import { logSentryError } from '~/sentry';
import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { loadStripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js';
import LineItems from '~/components/Cart/LineItems';
import CartSummary from '~/components/Cart/Summary';
import FootnoteText from '~/components/Nav/Cart/FootnoteText';
import useOnClickOutside from '~/hooks/components/use-on-click-outside';
import Logo from '~/assets/images/logos/logo.svg';
import CloseIcon from '~/assets/images/icons/x-white.svg';
import useCartNotes from '~/hooks/graphql/queries/use-cart-notes';
import { useLocalStorage } from '~/hooks/components/use-storage';
import { StringParam, useQueryParam } from 'use-query-params';
import { navigate } from 'gatsby';
import { useEmbeddedCheckout } from '~/context/EmbeddedCheckoutContext';
import { useTranslate } from '~/hooks/utils/use-translate';

const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PK_KEY ?? '', {
  stripeAccount: process.env.GATSBY_STRIPE_CONNECTED_ACCOUNT,
});

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  forwardSx?: SxProp['sx'];
};

const NavCartSlideOver = React.memo(function NavCartSlideover({ isOpen, handleClose, forwardSx = {} }: Props) {
  const { setValue: setCartChecker } = useLocalStorage('cart-number-check');
  const { cart, loadCart, modifyCart, forgetCart } = useCart();
  const { getStockAvailability } = useProduct();
  const { prepareCheckout } = useCheckout();
  const { trackCartViewed } = useAnalytics();
  const [_shareasaleClickId] = useQueryParam('sscid', StringParam);
  const { setCheckout, checkout } = useEmbeddedCheckout();
  const cartNote = useCartNotes();
  const [stockError, setStockError] = useState<string[]>([]);

  useEffect(() => {
    const loadAndTrackCart = async () => {
      try {
        await loadCart();
        // Get the shareasale click ID
        const shareasaleClickId = _shareasaleClickId || localStorage.getItem('shareasaleClickId');

        // Check if we need to update the cart's metadata to add the shareasale click ID.
        if (shareasaleClickId && cart?.metadata?.sscid !== shareasaleClickId) {
          localStorage.setItem('shareasaleClickId', shareasaleClickId);
          await modifyCart({ attributes: { metadata: { ...cart?.metadata, sscid: shareasaleClickId } } });
        }

        if (isOpen) {
          trackCartViewed();
        }
      } catch (error) {
        logSentryError(error, { source: 'Cart' });
      }
    };

    loadAndTrackCart();
  }, [loadCart, trackCartViewed, isOpen]);

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, handleClose);

  const handleComplete = async function (checkout: StripeEmbeddedCheckout, cart: OrderBig) {
    // @ts-ignore - embbededCheckout is not defined in the type but does exist
    const { embeddedCheckout } = checkout;

    checkout.unmount();

    navigate(`/order/confirmation/${cart.number}/${embeddedCheckout.checkoutSessionId}`);
    forgetCart();
  };

  useEffect(() => {
    setStockError([]);
  }, [cart.totalQuantity]);

  const checkCartAvailability = async (): Promise<boolean> => {
    if (!cart?.lineItems?.length) return true;

    // First, create all the stock check promises
    const stockCheckPromises = cart.lineItems.flatMap((item) => {
      if (item.isBundle) {
        const bundleItems =
          item?.parts && item.parts.length > 0
            ? item.parts.map((part) => part.variant.sku)
            : item.variant.sku.split('|');
        return bundleItems.map((sku) => ({
          item,
          checkPromise: getStockAvailability({ sku, channel: 'chord' }),
        }));
      }
      return [
        {
          item,
          checkPromise: getStockAvailability({ sku: item.variant.sku, channel: 'chord' }),
        },
      ];
    });

    // Then check all availability
    const stockChecks = await Promise.allSettled(
      stockCheckPromises.map(async ({ item, checkPromise }) => ({
        item,
        availability: await checkPromise,
      }))
    );

    const unavailable = stockChecks.reduce<{ item: LineItem; availability: VariantStockAvailability }[]>(
      (acc, result) => {
        if (result.status === 'fulfilled' && !result.value.availability.inStock) {
          acc.push(result.value);
        } else if (result.status === 'rejected') {
          console.error(`Failed to check stock for item:`, result.reason);
          // Note: We're being conservative here and treating failed checks as unavailable
          const failedItem = result.reason?.item;
          if (failedItem) acc.push(failedItem);
        }
        return acc;
      },
      []
    );

    if (unavailable.length > 0) {
      const itemNames = unavailable.map(
        (item) =>
          `${item.item.variant.name.toLowerCase()} ${item.item.variant.optionValues
            .map((option) => option.presentation)
            .reverse()
            .join(' ')}`
      );

      setStockError(itemNames);
      return false;
    }

    setStockError([]);
    return true;
  };

  const handleCartSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setApiError(null);
    setStockError([]);

    if (checkout) checkout.destroy();

    try {
      const isAvailable = await checkCartAvailability();
      if (!isAvailable) {
        setIsLoading(false);
        return;
      }

      const stripe = await stripePromise;
      if (!stripe) return;

      const cart = await prepareCheckout();

      setCartChecker(cart.number);
      const checkoutObj: StripeEmbeddedCheckout = await stripe?.initEmbeddedCheckout({
        // TODO(Theo): update package when available
        // @ts-ignore - the clientSecret is not defined in the type but does exist
        clientSecret: cart.clientSecret ?? '',
        onComplete: () => handleComplete(checkoutObj, cart),
      });

      setCheckout(checkoutObj);
      handleClose();
      await navigate(`/checkout/${cart.number}/`);
    } catch (error) {
      console.error('error', error);
      setApiError(translate('error.api.default'));
      logSentryError(error, { source: 'CheckoutButton' });
    }

    setIsLoading(false);
  };

  return (
    <aside
      aria-label="cart menu"
      sx={{
        '--transition-duration': '0.4s',
        '--reveal-transition': 'all var(--transition-duration) ease-in-out',
        '--width': ['100%', '37.6rem'],
        position: 'fixed',
        top: 0,
        right: isOpen ? '0%' : '-100%',
        width: 'var(--width)',
        height: '100%',
        backgroundColor: 'black',
        transition: 'var(--reveal-transition)',
        zIndex: 99,
        ...forwardSx,
      }}
      ref={containerRef}
    >
      <Container
        variant="navContainer"
        sx={{
          '--padding': '2.4rem',
          position: 'absolute',
          px: 'var(--padding)',
          display: 'flex',
          flexFlow: 'column nowrap',
          height: '100%',
          pt: 0,
          mt: 0,
          width: ['100vw', '100%'],
          color: 'white',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.6rem)',
          overflow: 'hidden',
        }}
      >
        <Flex
          sx={{
            height: '4.8rem',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            transition: isOpen ? null : 'width 0s var(--transition-duration)',
            width: isOpen ? ['100%', 'calc(var(--width) - var(--padding) * 2)'] : 0,
          }}
        >
          {/* <Box
            sx={{
              position: 'fixed',
              width: isOpen ? 'var(--width)' : 0,
              top: 0,
              right: 0,
              left: 'auto',
              overflow: 'hidden',
              transition: isOpen ? null : 'width 0s var(--transition-duration)',
              // The following to lines take care of having the masked nav bar overflowing to the
              // left (as opposed to the natural overflow to the right)
              direction: 'rtl',
              // here we rest the direction as it is inherited
              '> div': { direction: 'ltr' },
            }}
          > */}
          <Box
            sx={{
              display: ['flex', 'none'],
              opacity: [1, 0],
              width: '8rem',
              visibility: isOpen ? 'visible' : 'hidden',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'flex-start',
              svg: {
                width: '100%',
                height: 'auto',
                filter: 'invert()',
              },
            }}
          >
            <Logo />
          </Box>
          {/* </Box> */}
          <Text
            as="h2"
            variant="h6"
            sx={{
              opacity: [0, 1],
              fontSize: ['1.4rem'],
              position: 'relative',
              left: '0',
            }}
          >
            Cart
          </Text>
          <Button
            variant="bare"
            onClick={handleClose}
            aria-label="Close Cart"
            sx={{
              height: ['4.8rem'],
              width: ['4.8rem'],
              position: 'relative',
              minHeight: 0,
              minWidth: 0,
              m: 0,
              p: 0,
              right: '-1.6rem',

              zIndex: 50000,

              svg: { pointerEvents: 'none', strokeWidth: '1px' },
            }}
          >
            <CloseIcon />
          </Button>
        </Flex>

        <Flex
          sx={{
            minHeight: 'calc(100% - 6.4rem)',
            flexDirection: 'column',

            fieldset: { border: 0 },
          }}
        >
          <Box
            sx={{
              flexShrink: 10,
              flexGrow: 0,
              overflowY: 'auto',
              width: '100%',
              height: '100%',
              mb: '2rem',
              '--line-item-height': ['26vh', '16.7rem'],
              '--top-margin': ['2.5rem', '2rem'],
            }}
          >
            <LineItems />
          </Box>
          <Box sx={{ position: 'relative', bottom: 0 }}>
            <CartSummary
              apiError={apiError}
              handleCartSubmit={handleCartSubmit}
              isLoading={isLoading}
              cart={cart}
              stockError={stockError}
            />
            <FootnoteText cartNote={cartNote} />
          </Box>
        </Flex>
      </Container>
    </aside>
  );
});

export default NavCartSlideOver;
